import React, { useState, useEffect } from "react";
import { Card, Row, Col, Button, Modal, Badge, Spinner } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faClipboardList,
  faStar,
  faChartLine,
  faSmile,
  faPlus,
  faCheck,
  faEye,
  faUsers,
  faRocket,
  faLightbulb,
} from "@fortawesome/free-solid-svg-icons";
import { motion } from "framer-motion";
import axios from "axios";
import { ApiConfig } from "config/ApiConfig";
import { getApiConfig } from "helpers/utils";
import Loader from "components/Loader/Loader";
import { ShowToastError } from "components/Widgets/ShowToastError";
import GroupSelection from "./XSurvey/GroupSelection";
import MySurvey from "./XSurvey/MySurvey";
import "./Modules.css";

// Survey template icons and colors
const templateConfig = {
  csat_5: {
    icon: faSmile,
    color: "#22c55e",
    bgColor: "#dcfce7",
    title: "CSAT 5-Point Scale",
    description: "Classic 5-point customer satisfaction survey. Perfect for quick feedback collection with smiley faces or stars.",
    features: ["5-point rating scale", "Emoji or star options", "Quick to complete"],
  },
  csat_10: {
    icon: faChartLine,
    color: "#3b82f6",
    bgColor: "#dbeafe",
    title: "CSAT 10-Point Scale",
    description: "Detailed 10-point scale for more granular satisfaction measurement. Ideal for in-depth analysis.",
    features: ["10-point rating scale", "Detailed feedback", "Better granularity"],
  },
  nps_10: {
    icon: faRocket,
    color: "#8b5cf6",
    bgColor: "#ede9fe",
    title: "NPS Survey",
    description: "Net Promoter Score survey to measure customer loyalty. Industry standard for tracking promoters and detractors.",
    features: ["0-10 NPS scale", "Promoter tracking", "Industry standard"],
  },
};

export default function Modules() {
  const [featureData, setFeatureData] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [showGroupModal, setShowGroupModal] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [showCreateSurvey, setShowCreateSurvey] = useState(false);
  const [refreshCount, setRefreshCount] = useState(0);

  useEffect(() => {
    const fetchData = async () => {
      const config = getApiConfig();
      try {
        setIsLoading(true);
        const response = await axios.get(ApiConfig.xSurveyDetails, config);
        setFeatureData(response.data);
      } catch (error) {
        ShowToastError({ error });
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, [refreshCount, showCreateSurvey]);

  const handleUseTemplate = (templateKey) => {
    const templateDetails = featureData?.settings?.[templateKey];
    if (templateDetails) {
      setSelectedTemplate({ ...templateDetails, key: templateKey });
      setShowGroupModal(true);
    }
  };

  const getTemplateStatus = (templateKey) => {
    const template = featureData?.settings?.[templateKey];
    const groups = template?.selected_groups || [];
    return {
      isInUse: groups.length > 0,
      groupCount: groups.length,
      groups: groups.map(g => g.name || g),
    };
  };

  const remainingGroups = featureData?.remaining_groups || [];
  const ingestedGroups = featureData?.ingested_groups || [];

  // Get custom templates (not default ones)
  const defaultTemplateKeys = ["csat_5", "csat_10", "nps_10"];
  const customTemplates = Object.entries(featureData?.settings || {})
    .filter(([key]) => !defaultTemplateKeys.includes(key))
    .map(([key, data]) => ({
      key,
      name: data.name || key.replace(/_/g, " ").toUpperCase(),
      description: data.desc || "Custom survey template",
      groups: data.selected_groups?.map(g => g.name || g) || [],
      isInUse: (data.selected_groups?.length || 0) > 0,
    }));

  if (isLoading) {
    return <Loader />;
  }

  if (showCreateSurvey) {
    return (
      <div className="survey-templates-page">
        <div className="templates-header mb-4">
          <Button
            variant="outline-secondary"
            onClick={() => setShowCreateSurvey(false)}
            className="mb-3"
          >
            <FontAwesomeIcon icon="arrow-left" className="me-2" />
            Back to Templates
          </Button>
        </div>
        <MySurvey setShowSurvey={setShowCreateSurvey} />
      </div>
    );
  }

  return (
    <div className="survey-templates-page">
      {/* Page Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="templates-header"
      >
        <Card className="border-0 mb-4" style={{
          borderRadius: "16px",
          background: "#ffffff",
          boxShadow: "0 1px 3px rgba(0, 0, 0, 0.08), 0 1px 2px rgba(0, 0, 0, 0.06)",
          border: "1px solid rgba(226, 232, 240, 0.8)",
        }}>
          <Card.Body className="p-4">
            <div className="d-flex align-items-center justify-content-between flex-wrap gap-3">
              <div className="d-flex align-items-center gap-3">
                <div
                  style={{
                    width: "56px",
                    height: "56px",
                    borderRadius: "14px",
                    background: "linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    boxShadow: "0 4px 12px rgba(99, 102, 241, 0.3)",
                  }}
                >
                  <FontAwesomeIcon icon={faClipboardList} style={{ color: "#fff", fontSize: "1.5rem" }} />
                </div>
                <div>
                  <h1 style={{
                    fontSize: "1.75rem",
                    fontWeight: "700",
                    color: "#0f172a",
                    marginBottom: "0.25rem",
                    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
                  }}>
                    Survey Templates
                  </h1>
                  <p style={{
                    fontSize: "0.95rem",
                    color: "#64748b",
                    marginBottom: 0,
                  }}>
                    Choose from default templates or create your own custom surveys
                  </p>
                </div>
              </div>
              <Button
                onClick={() => setShowCreateSurvey(true)}
                style={{
                  background: "linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)",
                  border: "none",
                  borderRadius: "12px",
                  padding: "0.75rem 1.5rem",
                  fontWeight: "600",
                  boxShadow: "0 4px 12px rgba(99, 102, 241, 0.3)",
                }}
              >
                <FontAwesomeIcon icon={faPlus} className="me-2" />
                Create Custom Template
              </Button>
            </div>
          </Card.Body>
        </Card>
      </motion.div>

      {/* Default Templates Section */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.1 }}
      >
        <div className="d-flex align-items-center gap-2 mb-3">
          <FontAwesomeIcon icon={faStar} style={{ color: "#f59e0b" }} />
          <h5 style={{ margin: 0, fontWeight: "600", color: "#1e293b" }}>
            Default Templates
          </h5>
          <Badge bg="secondary" pill style={{ fontSize: "0.75rem" }}>
            3 Available
          </Badge>
        </div>
        <p style={{ color: "#64748b", fontSize: "0.9rem", marginBottom: "1.5rem" }}>
          Ready-to-use survey templates optimized for customer feedback collection. Assign them to your Zendesk groups to start collecting responses.
        </p>

        <Row className="g-4 mb-5">
          {defaultTemplateKeys.map((key, index) => {
            const config = templateConfig[key];
            const status = getTemplateStatus(key);

            return (
              <Col lg={4} md={6} key={key}>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: 0.1 + index * 0.1 }}
                >
                  <Card className="h-100 template-card" style={{
                    borderRadius: "16px",
                    border: "1px solid #e2e8f0",
                    overflow: "hidden",
                    transition: "all 0.3s ease",
                  }}>
                    {/* Card Header with Icon */}
                    <div style={{
                      background: config.bgColor,
                      padding: "1.5rem",
                      borderBottom: `2px solid ${config.color}20`,
                    }}>
                      <div className="d-flex align-items-center justify-content-between">
                        <div
                          style={{
                            width: "48px",
                            height: "48px",
                            borderRadius: "12px",
                            background: "#ffffff",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                          }}
                        >
                          <FontAwesomeIcon icon={config.icon} style={{ color: config.color, fontSize: "1.25rem" }} />
                        </div>
                        {status.isInUse && (
                          <Badge
                            bg="success"
                            style={{
                              borderRadius: "20px",
                              padding: "0.5rem 0.75rem",
                              fontSize: "0.75rem",
                            }}
                          >
                            <FontAwesomeIcon icon={faCheck} className="me-1" />
                            In Use
                          </Badge>
                        )}
                      </div>
                      <h5 style={{
                        marginTop: "1rem",
                        marginBottom: "0.25rem",
                        fontWeight: "700",
                        color: "#1e293b",
                      }}>
                        {config.title}
                      </h5>
                    </div>

                    <Card.Body className="d-flex flex-column" style={{ padding: "1.25rem" }}>
                      <p className="template-description" style={{ color: "#64748b", fontSize: "0.9rem" }}>
                        {config.description}
                      </p>

                      {/* Features */}
                      <div className="template-features mb-3">
                        {config.features.map((feature, i) => (
                          <div key={i} className="d-flex align-items-center gap-2 mb-1">
                            <FontAwesomeIcon icon={faCheck} style={{ color: config.color, fontSize: "0.75rem" }} />
                            <span style={{ fontSize: "0.85rem", color: "#475569" }}>{feature}</span>
                          </div>
                        ))}
                      </div>

                      {/* Groups Using - Always reserve space */}
                      <div className="template-groups-section mb-3">
                        {status.isInUse ? (
                          <div className="p-2 rounded" style={{ background: "#f8fafc", border: "1px solid #e2e8f0" }}>
                            <div className="d-flex align-items-center gap-2">
                              <FontAwesomeIcon icon={faUsers} style={{ color: "#64748b", fontSize: "0.8rem" }} />
                              <span style={{ fontSize: "0.8rem", color: "#64748b" }}>
                                Active in {status.groupCount} group{status.groupCount > 1 ? "s" : ""}
                              </span>
                            </div>
                            <div className="mt-1 d-flex flex-wrap gap-1">
                              {status.groups.slice(0, 3).map((group, i) => (
                                <Badge key={i} bg="light" text="dark" style={{ fontSize: "0.7rem", fontWeight: "500" }}>
                                  {group}
                                </Badge>
                              ))}
                              {status.groups.length > 3 && (
                                <Badge bg="light" text="primary" style={{ fontSize: "0.7rem" }}>
                                  +{status.groups.length - 3} more
                                </Badge>
                              )}
                            </div>
                          </div>
                        ) : (
                          <div className="p-2 rounded" style={{ background: "#f8fafc", border: "1px dashed #e2e8f0" }}>
                            <div className="d-flex align-items-center gap-2">
                              <FontAwesomeIcon icon={faUsers} style={{ color: "#94a3b8", fontSize: "0.8rem" }} />
                              <span style={{ fontSize: "0.8rem", color: "#94a3b8" }}>
                                Not assigned to any group yet
                              </span>
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Action Button */}
                      <Button
                        className="template-action-btn"
                        onClick={() => handleUseTemplate(key)}
                        variant={status.isInUse ? "outline-primary" : "primary"}
                        style={{
                          borderRadius: "10px",
                          padding: "0.625rem 1rem",
                          fontWeight: "600",
                          width: "100%",
                          background: status.isInUse ? "transparent" : `linear-gradient(135deg, ${config.color} 0%, ${config.color}dd 100%)`,
                          borderColor: config.color,
                          color: status.isInUse ? config.color : "#fff",
                        }}
                      >
                        {status.isInUse ? (
                          <>
                            <FontAwesomeIcon icon={faUsers} className="me-2" />
                            Manage Groups
                          </>
                        ) : (
                          <>
                            <FontAwesomeIcon icon={faRocket} className="me-2" />
                            Use Template
                          </>
                        )}
                      </Button>
                    </Card.Body>
                  </Card>
                </motion.div>
              </Col>
            );
          })}
        </Row>
      </motion.div>

      {/* Custom Templates Section */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.3 }}
      >
        <div className="d-flex align-items-center gap-2 mb-3">
          <FontAwesomeIcon icon={faLightbulb} style={{ color: "#f59e0b" }} />
          <h5 style={{ margin: 0, fontWeight: "600", color: "#1e293b" }}>
            Custom Templates
          </h5>
          <Badge bg="secondary" pill style={{ fontSize: "0.75rem" }}>
            {customTemplates.length} Created
          </Badge>
        </div>
        <p style={{ color: "#64748b", fontSize: "0.9rem", marginBottom: "1.5rem" }}>
          Your custom survey templates. Create personalized surveys with your own questions and branding.
        </p>

        {customTemplates.length === 0 ? (
          <Card className="border-0 text-center py-5" style={{
            borderRadius: "16px",
            background: "linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)",
            border: "2px dashed #e2e8f0",
          }}>
            <Card.Body>
              <div
                style={{
                  width: "64px",
                  height: "64px",
                  borderRadius: "16px",
                  background: "#e2e8f0",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  margin: "0 auto 1rem",
                }}
              >
                <FontAwesomeIcon icon={faPlus} style={{ color: "#64748b", fontSize: "1.5rem" }} />
              </div>
              <h5 style={{ color: "#475569", fontWeight: "600" }}>No Custom Templates Yet</h5>
              <p style={{ color: "#64748b", maxWidth: "400px", margin: "0 auto 1.5rem" }}>
                Create your first custom survey template with personalized questions, rating scales, and branding.
              </p>
              <Button
                onClick={() => setShowCreateSurvey(true)}
                style={{
                  background: "linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)",
                  border: "none",
                  borderRadius: "12px",
                  padding: "0.75rem 1.5rem",
                  fontWeight: "600",
                }}
              >
                <FontAwesomeIcon icon={faPlus} className="me-2" />
                Create Your First Template
              </Button>
            </Card.Body>
          </Card>
        ) : (
          <Row className="g-4">
            {customTemplates.map((template, index) => (
              <Col lg={4} md={6} key={template.key}>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: 0.1 + index * 0.1 }}
                >
                  <Card className="h-100 template-card" style={{
                    borderRadius: "16px",
                    border: "1px solid #e2e8f0",
                    overflow: "hidden",
                  }}>
                    <div style={{
                      background: "linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%)",
                      padding: "1.5rem",
                      borderBottom: "1px solid #e2e8f0",
                    }}>
                      <div className="d-flex align-items-center justify-content-between">
                        <div
                          style={{
                            width: "48px",
                            height: "48px",
                            borderRadius: "12px",
                            background: "#ffffff",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                          }}
                        >
                          <FontAwesomeIcon icon={faClipboardList} style={{ color: "#6366f1", fontSize: "1.25rem" }} />
                        </div>
                        {template.isInUse && (
                          <Badge bg="success" style={{ borderRadius: "20px", padding: "0.5rem 0.75rem" }}>
                            <FontAwesomeIcon icon={faCheck} className="me-1" />
                            In Use
                          </Badge>
                        )}
                      </div>
                      <h5 style={{ marginTop: "1rem", marginBottom: 0, fontWeight: "700", color: "#1e293b" }}>
                        {template.name}
                      </h5>
                    </div>

                    <Card.Body className="d-flex flex-column" style={{ padding: "1.25rem" }}>
                      <p style={{ color: "#64748b", fontSize: "0.9rem", flex: 1 }}>
                        {template.description}
                      </p>

                      {template.isInUse && (
                        <div className="mb-3 p-2 rounded" style={{ background: "#f8fafc", border: "1px solid #e2e8f0" }}>
                          <div className="d-flex align-items-center gap-2">
                            <FontAwesomeIcon icon={faUsers} style={{ color: "#64748b", fontSize: "0.8rem" }} />
                            <span style={{ fontSize: "0.8rem", color: "#64748b" }}>
                              Active in {template.groups.length} group{template.groups.length > 1 ? "s" : ""}
                            </span>
                          </div>
                        </div>
                      )}

                      <Button
                        onClick={() => handleUseTemplate(template.key)}
                        variant={template.isInUse ? "outline-primary" : "primary"}
                        style={{
                          borderRadius: "10px",
                          padding: "0.625rem 1rem",
                          fontWeight: "600",
                          width: "100%",
                        }}
                      >
                        {template.isInUse ? (
                          <>
                            <FontAwesomeIcon icon={faUsers} className="me-2" />
                            Manage Groups
                          </>
                        ) : (
                          <>
                            <FontAwesomeIcon icon={faRocket} className="me-2" />
                            Use Template
                          </>
                        )}
                      </Button>
                    </Card.Body>
                  </Card>
                </motion.div>
              </Col>
            ))}
          </Row>
        )}
      </motion.div>

      {/* Group Selection Modal */}
      <Modal
        show={showGroupModal}
        onHide={() => setShowGroupModal(false)}
        centered
        size="lg"
      >
        <Modal.Header closeButton style={{ borderBottom: "1px solid #e2e8f0" }}>
          <Modal.Title style={{ fontWeight: "600" }}>
            Assign Template to Zendesk Groups
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedTemplate && (
            <GroupSelection
              app_module={{ name: "SurveyX" }}
              survey_name={selectedTemplate}
              remaining_groups={remainingGroups}
              allowed_groups={selectedTemplate?.selected_groups || []}
              closeModal={setShowGroupModal}
              updateGroups={() => {}}
              setCount={() => setRefreshCount(c => c + 1)}
              details={featureData}
            />
          )}
        </Modal.Body>
      </Modal>
    </div>
  );
}
